import { IonIcon } from '@ionic/react';
import { lockClosed } from 'ionicons/icons';
import React, { useMemo } from 'react';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SkillStats } from '../../models/userStats';
import WeeklyRatingChart, { WeeklyRating } from './WeeklyRatingChart';

import 'swiper/css';
import 'swiper/css/pagination';

interface SwipableStatsCardProps {
    stats: SkillStats;
    weeklyData: WeeklyRating[];
    title: string;
    subtitle?: string;
    gradient?: string;
    isSubscribed?: boolean;
    onPaywallClick?: () => void;
}

const SwipableStatsCard: React.FC<SwipableStatsCardProps> = ({
    stats,
    weeklyData,
    title,
    subtitle,
    gradient = 'from-purple-600 to-indigo-600',
    isSubscribed = true,
    onPaywallClick
}) => {
    const categories = [
        { key: 'rail' as keyof SkillStats, label: 'Rail/Jib', icon: '🛹', locked: false },
        { key: 'jump' as keyof SkillStats, label: 'Jump', icon: '🚀', locked: !isSubscribed },
        { key: 'style' as keyof SkillStats, label: 'Style', icon: '✨', locked: !isSubscribed },
        { key: 'off_snow' as keyof SkillStats, label: 'Off-Snow', icon: '💪', locked: !isSubscribed },
        { key: 'mountain' as keyof SkillStats, label: 'Freeride', icon: '⛰️', locked: !isSubscribed },
        { key: 'halfpipe' as keyof SkillStats, label: 'Halfpipe', icon: '🏂', locked: !isSubscribed }
    ];

    const visibleStats = categories.filter(cat => !cat.locked);
    const lockedStats = categories.filter(cat => cat.locked);

    const averageScore = useMemo(() => {
        if (isSubscribed) {
            const values = Object.values(stats) as number[];
            return Math.round(values.reduce((sum, val) => sum + val, 0) / values.length);
        }
        return stats.rail || 0;
    }, [stats, isSubscribed]);

    // Numerical Stats Slide Content
    const NumericalStatsSlide = () => (
        <div className="relative z-10">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
                <div>
                    <h3 className="text-white font-bold text-lg">{title}</h3>
                    {subtitle && (
                        <p className="text-white/70 text-xs mt-1">{subtitle}</p>
                    )}
                </div>
                <div className="text-right">
                    <div className="text-white font-bold text-3xl">{averageScore}</div>
                    <div className="text-white/70 text-xs">
                        {isSubscribed ? 'Rating' : 'Rail/Jib'}
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-3">
                {/* Visible stats */}
                {visibleStats.map(category => (
                    <div
                        key={category.key}
                        className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20"
                    >
                        <div className="text-2xl mb-1">{category.icon}</div>
                        <div className="text-white font-bold text-lg">
                            {stats[category.key]}
                        </div>
                        <div className="text-white/70 text-xs font-medium">
                            {category.label}
                        </div>
                    </div>
                ))}

                {/* Locked stats with mini paywall */}
                {lockedStats.map(category => (
                    <div
                        key={category.key}
                        className="relative bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10 cursor-pointer hover:bg-white/10 transition-all"
                        onClick={onPaywallClick}
                    >
                        {/* Blurred background stat */}
                        <div className="blur-sm opacity-30">
                            <div className="text-2xl mb-1">{category.icon}</div>
                            <div className="text-white font-bold text-lg">
                                {Math.floor(Math.random() * 100)}
                            </div>
                            <div className="text-white/70 text-xs font-medium">
                                {category.label}
                            </div>
                        </div>

                        {/* Lock overlay */}
                        <div className="absolute inset-0 flex flex-col justify-center items-center bg-black/40 backdrop-blur-sm rounded-xl">
                            <IonIcon icon={lockClosed} className="text-white/80 text-lg mb-1" />
                            <span className="text-white/60 text-xs font-medium">Pro</span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Call to action for non-subscribers */}
            {!isSubscribed && (
                <div className="mt-4 pt-3 border-t border-white/20">
                    <button
                        onClick={onPaywallClick}
                        className="w-full bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg p-2 border border-white/20 transition-all text-center"
                    >
                        <span className="text-white text-sm font-medium">
                            🔓 Unlock all {lockedStats.length} elements
                        </span>
                    </button>
                </div>
            )}
        </div>
    );

    // Check if weekly data is empty (coming soon state)
    const isWeeklyComingSoon = !weeklyData || weeklyData.length === 0;

    // Weekly Chart Slide Content
    const WeeklyChartSlide = () => (
        <div className="relative z-10">
            {/* Header */}
            <div className="mb-2">
                <h3 className="text-white font-bold text-lg">Weekly Progress</h3>
                <p className="text-white/70 text-xs">Your rating over time</p>
            </div>

            {/* Chart */}
            {isWeeklyComingSoon ? (
                <div className="flex flex-col justify-center items-center h-[180px] bg-white/5 rounded-xl border border-white/10">
                    <span className="text-4xl mb-3">🚀</span>
                    <span className="text-white font-bold text-lg">Coming Soon</span>
                    <span className="text-white/60 text-sm mt-1 text-center px-4">
                        Weekly progress tracking is on its way!
                    </span>
                </div>
            ) : isSubscribed ? (
                <WeeklyRatingChart
                    data={weeklyData}
                    height={180}
                    lineColor="#8FBC8F"
                    showGrid={true}
                />
            ) : (
                <div className="relative">
                    {/* Blurred chart placeholder */}
                    <div className="blur-md opacity-50 pointer-events-none">
                        <WeeklyRatingChart
                            data={weeklyData}
                            height={180}
                            lineColor="#8FBC8F"
                            showGrid={true}
                        />
                    </div>
                    {/* Lock overlay */}
                    <div
                        className="absolute inset-0 flex flex-col justify-center items-center bg-black/40 backdrop-blur-sm rounded-xl cursor-pointer"
                        onClick={onPaywallClick}
                    >
                        <IonIcon icon={lockClosed} className="text-white/80 text-3xl mb-2" />
                        <span className="text-white font-medium text-sm">Unlock Progress Tracking</span>
                        <span className="text-white/60 text-xs mt-1">Get Tow Rope Pass</span>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${gradient} shadow-xl`}>
            {/* Animated background effect */}
            <div className="absolute inset-0 bg-white/5 backdrop-blur-3xl"></div>

            <Swiper
                modules={[Pagination]}
                pagination={{
                    clickable: true,
                    bulletClass: 'swiper-pagination-bullet !bg-white/40',
                    bulletActiveClass: 'swiper-pagination-bullet-active !bg-white'
                }}
                spaceBetween={0}
                slidesPerView={1}
                className="w-full"
            >
                <SwiperSlide>
                    <div className="p-4 pb-8">
                        <NumericalStatsSlide />
                    </div>
                </SwiperSlide>
                <SwiperSlide>
                    <div className="p-4 pb-8">
                        <WeeklyChartSlide />
                    </div>
                </SwiperSlide>
            </Swiper>

            {/* Custom pagination styling */}
            <style>{`
                .swiper-pagination {
                    bottom: 8px !important;
                }
                .swiper-pagination-bullet {
                    width: 8px;
                    height: 8px;
                    opacity: 1;
                }
                .swiper-pagination-bullet-active {
                    width: 20px;
                    border-radius: 4px;
                }
            `}</style>
        </div>
    );
};

export default SwipableStatsCard;
